import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, CircularProgress,
  Alert, TextField, InputAdornment, Button, Tabs, Tab, Pagination, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Visibility, Edit, Delete, Search, Add, Send, Email, FileCopy } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/AppHeader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function IBNKlimaListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [protokolle, setProtokolle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0 = Alle, 1 = Entwürfe, 2 = Gesendet
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(20);
  
  // Email-Dialog States
  const [emailDialog, setEmailDialog] = useState({
    open: false,
    email: '',
    protokoll: null
  });

  useEffect(() => {
    fetchProtokolle();
  }, []);

  const fetchProtokolle = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/ibn-klima-protocols`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const protokolleData = response.data || [];
      
      console.log('📋 Protokolle geladen:', {
        total: protokolleData.length,
        entwuerfe: protokolleData.filter(p => p.status === 'draft').length,
        gesendet: protokolleData.filter(p => p.status === 'submitted').length,
        ids: protokolleData.map(p => p._id)
      });
      
      // Sort by date (newest first)
      const sorted = protokolleData.sort((a, b) => 
        new Date(b.created_at || b.datum) - new Date(a.created_at || a.datum)
      );
      setProtokolle(sorted);
      setError('');
    } catch (err) {
      console.error('Error fetching protokolle:', err);
      setError('Fehler beim Laden der Protokolle');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie dieses Protokoll wirklich löschen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/ibn-klima-protocols/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProtokolle(protokolle.filter(p => p._id !== id));
    } catch (err) {
      console.error('Error deleting protokoll:', err);
      alert('Fehler beim Löschen des Protokolls');
    }
  };

  const handleView = (protokoll) => {
    // Open PDF if exists
    if (protokoll.pdf_id) {
      const token = localStorage.getItem('token');
      window.open(`${API_URL}/ibn-klima-protocols/pdf/${protokoll.pdf_id}?token=${token}`, '_blank');
    } else {
      alert('PDF nicht verfügbar');
    }
  };

  const handleEdit = (protokoll) => {
    // Navigate to form with existing data for editing
    navigate('/protocol/ibn-klima-form', { 
      state: { 
        editMode: true,
        formData: protokoll
      }
    });
  };

  const handleSend = async (protokoll) => {
    if (!window.confirm('Möchten Sie diesen Entwurf jetzt als Protokoll versenden?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Update status to "submitted" - this will trigger PDF generation and email sending
      await axios.put(
        `${API_URL}/ibn-klima-protocols/${protokoll._id}`,
        { ...protokoll, status: 'submitted' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Protokoll erfolgreich versendet!');
      fetchProtokolle(); // Refresh list
    } catch (err) {
      console.error('Error sending protokoll:', err);
      alert('Fehler beim Versenden des Protokolls');
    }
  };

  const handleResendEmail = (protokoll) => {
    setEmailDialog({
      open: true,
      email: protokoll.kunde_email || '',
      protokoll: protokoll
    });
  };

  const handleSendEmailConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      const { email, protokoll } = emailDialog;

      if (!email || !email.trim()) {
        alert('Bitte geben Sie eine Email-Adresse ein');
        return;
      }

      // Call resend endpoint
      await axios.post(
        `${API_URL}/ibn-klima-protocols/${protokoll._id}/resend`,
        { email: email.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Email erfolgreich versendet!');
      setEmailDialog({ open: false, email: '', protokoll: null });
    } catch (err) {
      console.error('Error resending email:', err);
      alert('Fehler beim Versenden der Email: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUseAsTemplate = async (protokoll) => {
    if (!window.confirm('Dieses Protokoll als Vorlage für ein neues Protokoll verwenden?\n\nEs werden nur Kundendaten und Gerätedaten übernommen.')) {
      return;
    }

    // Nur Kundendaten und Gerätedaten kopieren
    const templateData = {
      // Kundendaten
      kunde_name: protokoll.kunde_name || '',
      kundennummer: protokoll.kundennummer || protokoll.ku_nr || '',  // IBN verwendet kundennummer
      kunde_strasse: protokoll.kunde_strasse || '',
      kunde_plz: protokoll.kunde_plz || '',
      kunde_ort: protokoll.kunde_ort || '',
      kunde_email: protokoll.kunde_email || '',
      standort: protokoll.standort || '',
      hersteller: protokoll.hersteller || 'Kaysun',
      
      // Innengeräte (ohne Messwerte und Checkboxen)
      innengeraete: (protokoll.innengeraete || []).map(ig => ({
        montageort: ig.montageort || '',
        hersteller: ig.hersteller || '',
        hersteller_sonstiger: ig.hersteller_sonstiger || '',
        modell: ig.modellnummer || ig.modell || '',
        seriennummer: ig.seriennummer || '',
        kaeltemittel: ig.kaeltemittel || '',
        fuellmenge: ig.fuellmenge || '',
        betriebsmodus: ig.betriebsmodus || 'kühlen',
        luefterstufe: ig.luefterstufe || '',
        // Messwerte NICHT kopieren
        ansaug_temp: '',
        ausblas_temp: '',
        // Checkboxen NICHT kopieren - mit korrekter Struktur
        arbeiten: {
          luftfilter: false,
          verdampfer: false,
          kondenswasserpumpe: false,
          kondenswasserwanne: false,
          kondenswasserablauf_geprueft: false,
          elektrische_anschluesse: false,
          geblaesemotor: false,
          ausblaslamellen: false,
          gehaeuse: false
        }
      })),
      
      // Außengerät (ohne Messwerte und Checkboxen)
      aussengeraet: protokoll.aussengeraet ? {
        modell: protokoll.aussengeraet.modellnummer || protokoll.aussengeraet.modell || '',
        seriennummer: protokoll.aussengeraet.seriennummer || '',
        // Messwerte NICHT kopieren
        aussen_temp: '',
        stromaufnahme: '',
        // Checkboxen NICHT kopieren - mit korrekter Struktur
        arbeiten: {
          waermetauscher: false,
          kaeltetechnische_anschluesse: false,
          kondensatwanne: false,
          innenraum: false,
          lueftermotor: false,
          isolierung: false,
          elektrische_anschluesse: false,
          sicherheitseinrichtungen: false,
          kompressor: false,
          kaeltemitteldruecke: false,
          leistungsmessung: false
        }
      } : {
        modell: '',
        seriennummer: '',
        aussen_temp: '',
        stromaufnahme: '',
        arbeiten: {
          waermetauscher: false,
          kaeltetechnische_anschluesse: false,
          kondensatwanne: false,
          innenraum: false,
          lueftermotor: false,
          isolierung: false,
          elektrische_anschluesse: false,
          sicherheitseinrichtungen: false,
          kompressor: false,
          kaeltemitteldruecke: false,
          leistungsmessung: false
        }
      },
      
      // Neue leere Felder für das neue Protokoll
      auftragsnummer: '',
      datum: new Date().toISOString().split('T')[0],
      bilder: [],
      monteur_unterschrift: '',
      kunde_unterschrift: '',
      kunde_name_unterschrift: '',
      hinweise: '',
      status: 'draft',
      
      // Dichteprüfung zurücksetzen
      dichtepr_notwendig: 'nein',
      dichtepr_durchgefuehrt: 'nein',
      pruefintervall: '12',
      anlage_status: 'dicht',
      
      // Kältemittel-Daten vom Original-Protokoll übernehmen
      nachfuellmenge: protokoll.nachfuellmenge || '0',
      gesamtfuellmenge: protokoll.gesamtfuellmenge || '',
      kaeltemittel: protokoll.kaeltemittel || 'R32',
      fuellmenge_typenschild: protokoll.fuellmenge_typenschild || '',
      
      // Kundenzufriedenheit zurücksetzen
      kunde_zufrieden: 'ja',
      kunde_zufrieden_begruendung: ''
    };

    navigate('/protocol/ibn-klima-form', { state: { templateData } });
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Filter based on search query
  const filteredProtokolle = protokolle.filter(p => {
    const matchesSearch = !searchQuery || 
      p.kunde_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.kundennummer || p.ku_nr)?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.auftragsnummer?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filter by tab
    if (tabValue === 1) return p.status === 'draft' && matchesSearch;
    if (tabValue === 2) return p.status === 'submitted' && matchesSearch;
    return matchesSearch;
  });

  // Sort by date descending (newest first)
  const sortedProtokolle = [...filteredProtokolle].sort((a, b) => {
    return new Date(b.created_at || b.datum) - new Date(a.created_at || a.datum);
  });

  // Paginate
  const totalPages = Math.ceil(sortedProtokolle.length / rowsPerPage);
  const paginatedProtokolle = sortedProtokolle.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  if (loading) {
    return (
      <Box>
        <AppHeader />
        <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <AppHeader />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Inbetriebnahme Klimaanlage
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/protocol/ibn-klima-customer-search')}
            >
              Neues Protokoll
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Suche nach Kunde, Kundennummer oder Auftragsnummer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label={`Alle (${protokolle.length})`} />
            <Tab label={`Entwürfe (${protokolle.filter(p => p.status === 'draft').length})`} />
            <Tab label={`Gesendet (${protokolle.filter(p => p.status === 'submitted').length})`} />
          </Tabs>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Datum</TableCell>
                  <TableCell>Auftragsnummer</TableCell>
                  <TableCell>Kunde</TableCell>
                  <TableCell>Kd-Nr</TableCell>
                  <TableCell>Hersteller</TableCell>
                  <TableCell>Monteur</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProtokolle.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Keine Protokolle gefunden
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedProtokolle.map((protokoll) => (
                    <TableRow key={protokoll._id} hover>
                      <TableCell>{formatDate(protokoll.datum)}</TableCell>
                      <TableCell>{protokoll.auftragsnummer || '-'}</TableCell>
                      <TableCell>{protokoll.kunde_name}</TableCell>
                      <TableCell>{protokoll.kundennummer || protokoll.ku_nr || '-'}</TableCell>
                      <TableCell>
                        {protokoll.hersteller === 'Sonstiger' 
                          ? protokoll.hersteller_sonstiger || 'Sonstiger'
                          : protokoll.hersteller || '-'}
                      </TableCell>
                      <TableCell>{protokoll.monteur || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={protokoll.status === 'draft' ? 'Entwurf' : 'Gesendet'}
                          color={protokoll.status === 'draft' ? 'warning' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {protokoll.status === 'draft' ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(protokoll)}
                              title="Bearbeiten"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(protokoll._id)}
                              title="Löschen"
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <Tooltip title="PDF anzeigen">
                              <IconButton
                                size="small"
                                onClick={() => handleView(protokoll)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Email erneut senden">
                              <IconButton
                                size="small"
                                onClick={() => handleResendEmail(protokoll)}
                                color="info"
                              >
                                <Email />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Als Vorlage verwenden">
                              <IconButton
                                size="small"
                                onClick={() => handleUseAsTemplate(protokoll)}
                                color="success"
                              >
                                <FileCopy />
                              </IconButton>
                            </Tooltip>
                            {user?.role === 'admin' && (
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(protokoll._id)}
                                title="Löschen"
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Pagination 
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </Paper>
      </Container>

      {/* Email-Dialog */}
      <Dialog 
        open={emailDialog.open} 
        onClose={() => setEmailDialog({ open: false, email: '', protokoll: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Email-Adresse bestätigen</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Bitte bestätigen oder ändern Sie die Email-Adresse für den Versand:
            </Typography>
            <TextField
              fullWidth
              label="Email-Adresse"
              type="email"
              value={emailDialog.email}
              onChange={(e) => setEmailDialog({ ...emailDialog, email: e.target.value })}
              autoFocus
              placeholder="beispiel@email.de"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEmailDialog({ open: false, email: '', protokoll: null })}
            color="inherit"
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleSendEmailConfirm}
            variant="contained"
            color="primary"
            startIcon={<Email />}
          >
            Senden
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
